import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import { DocumentRecord, DocumentKind, TransactionCandidate, DocumentDetailsRequest, DocumentUploadResult, BankTransaction } from '../types';
import documentService, { DocumentFilter } from '../services/documentService';
import bankingService from '../services/bankingService';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Dokumente: Belege, Briefe, Verträge — was die App scannt oder hier hochgeladen wird.
 *
 * Nur Belege gehören an einen Kontoumsatz. Unterwegs entsteht der Beleg meist vor dem Umsatz,
 * eine Kartenzahlung steht erst Tage später auf dem Auszug. Offene Belege sind deshalb kein
 * Fehler, sondern warten; der nächste Kontoimport ordnet zu, was eindeutig passt.
 */

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('de-DE') : '—';

const centsToInput = (cents?: number | null): string =>
  cents == null ? '' : (cents / 100).toFixed(2).replace('.', ',');
const inputToCents = (v: string): number | null => {
  const cleaned = v.trim().replace(/\s|€/g, '');
  if (!cleaned) return null;
  // "1.234,56" und "1234,56" deutsch, "1234.56" englisch
  const normalized = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  const n = parseFloat(normalized);
  return isNaN(n) ? null : Math.round(n * 100);
};

const KIND_LABEL: Record<DocumentKind, string> = {
  RECEIPT: 'Beleg',
  LETTER: 'Brief',
  CONTRACT: 'Vertrag',
  OTHER: 'Sonstiges',
};
const KIND_PLURAL: Record<DocumentKind, string> = {
  RECEIPT: 'Belege',
  LETTER: 'Briefe',
  CONTRACT: 'Verträge',
  OTHER: 'Sonstiges',
};
/** Wie der Aussteller je Art heißt. */
const ISSUER_LABEL: Record<DocumentKind, string> = {
  RECEIPT: 'Händler',
  LETTER: 'Absender',
  CONTRACT: 'Vertragspartner',
  OTHER: 'Aussteller',
};
const KINDS: DocumentKind[] = ['RECEIPT', 'LETTER', 'CONTRACT', 'OTHER'];

const SOURCE_LABEL: Record<string, string> = { SCAN: 'Scan', UPLOAD: 'Datei', SHARE: 'Geteilt' };

type Prefilled = NonNullable<DocumentUploadResult['prefilled']>;
const PREFILL_FIELD_LABEL: Record<keyof Prefilled, string> = {
  amountCents: 'Betrag',
  documentDate: 'Datum',
  vatRatePercent: 'Steuersatz',
  issuer: 'Händler',
};

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-full text-xs font-medium border"
    style={{
      background: active ? 'var(--text-primary)' : 'var(--input-bg)',
      color: active ? 'var(--surface-bg)' : 'var(--text-primary)',
      borderColor: 'var(--surface-border)',
    }}>
    {children}
  </button>
);

const Documents: React.FC = () => {
  usePageTitle('Dokumente');
  const [params, setParams] = useSearchParams();
  const transactionId = params.get('transactionId') ? Number(params.get('transactionId')) : undefined;

  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [kind, setKind] = useState<DocumentKind | undefined>(undefined);
  const [unmatched, setUnmatched] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DocumentRecord | null>(null);
  /** Was der letzte Upload selbst ergänzt hat - markiert im Dialog, solange er offen ist. */
  const [prefilled, setPrefilled] = useState<Prefilled>({});
  /** Der Umsatz, zu dem der Beleg gesucht wird - damit beim Suchen in den Dateien klar bleibt, welcher. */
  const [transaction, setTransaction] = useState<BankTransaction | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTransaction(null);
    if (!transactionId) return;
    bankingService.getTransaction(transactionId).then(setTransaction)
      .catch(() => toast.error('Der Umsatz konnte nicht geladen werden.'));
  }, [transactionId]);

  const load = useCallback(async () => {
    const filter: DocumentFilter = transactionId
      ? { transactionId }
      : { kind, unmatched: unmatched || undefined, search: search.trim() || undefined, limit: 300 };
    try {
      setDocuments(await documentService.getDocuments(filter));
    } catch {
      toast.error('Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [transactionId, kind, unmatched, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    let matched = 0;
    let last: DocumentUploadResult | null = null;
    // Zu einem Umsatz hochgeladen ist es ein Beleg; sonst die Art, nach der gerade gefiltert wird
    const uploadKind: DocumentKind = transactionId ? 'RECEIPT' : kind ?? 'RECEIPT';
    try {
      for (const file of list) {
        const result = await documentService.upload(file, { kind: uploadKind }, transactionId);
        last = result;
        if (result.document.transactionId) matched++;
      }
      const read = list.length === 1 && last?.prefilled
        ? (Object.keys(last.prefilled) as (keyof Prefilled)[])
          .filter(f => last?.prefilled?.[f] === 'FILE').map(f => PREFILL_FIELD_LABEL[f])
        : [];
      toast.success(
        `${list.length} ${list.length === 1 ? KIND_LABEL[uploadKind] : KIND_PLURAL[uploadKind]} abgelegt` +
        (transactionId ? ' und dem Umsatz zugeordnet.' : matched > 0 ? `, ${matched} zugeordnet.` : '.') +
        (read.length > 0 ? ` Aus der Datei gelesen: ${read.join(', ')}.` : ''),
      );
      await load();
      // Ein einzelnes Dokument geht gleich zum Prüfen und Ergänzen auf
      if (list.length === 1 && last) {
        setPrefilled(last.prefilled ?? {});
        setSelected(last.document);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Hochladen fehlgeschlagen.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) upload(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}>
      <PageHeader
        title="Dokumente"
        subtitle="Belege, Briefe und Verträge ablegen — Belege den Kontoumsätzen zuordnen"
        action={
          <>
            <input ref={fileInput} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/heic"
              className="hidden" onChange={e => e.target.files && upload(e.target.files)} />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
              {uploading ? 'Lädt hoch …' : '+ Hochladen'}
            </button>
          </>
        }
      />

      {transactionId && (
        <div className="rounded-xl p-4 mb-4 text-sm flex flex-wrap items-start justify-between gap-4"
          style={{ background: 'var(--surface-bg)', border: '2px solid var(--badge-amber-text)', color: 'var(--text-primary)' }}>
          {transaction ? (
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--badge-amber-text)' }}>
                Gesucht: der Beleg zu diesem Umsatz
              </div>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="text-2xl font-bold nums-tabular">{euro(Math.abs(transaction.amountCents))}</span>
                <span className="text-base font-semibold">{transaction.counterparty || '—'}</span>
              </div>
              <div className="mt-1 nums-tabular" style={{ color: 'var(--text-muted)' }}>
                gebucht am {formatDate(transaction.bookingDate)}
                {transaction.documentDate && <> · Rechnungsdatum {formatDate(transaction.documentDate)}</>}
                {transaction.accountName && <> · {transaction.accountName}</>}
              </div>
              {transaction.reference && (
                <div className="mt-1 break-words" style={{ color: 'var(--text-muted)' }}>
                  Verwendungszweck: {transaction.reference}
                </div>
              )}
              <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                Was du jetzt hochlädst, hängt direkt an diesem Umsatz.
              </div>
            </div>
          ) : (
            <span>Belege zu Umsatz #{transactionId}. Was du jetzt hochlädst, hängt direkt an diesem Umsatz.</span>
          )}
          <div className="flex gap-4">
            <Link to="/banking" className="underline" style={{ color: 'var(--text-muted)' }}>zurück zu den Konten</Link>
            <button className="underline" style={{ color: 'var(--text-muted)' }}
              onClick={() => setParams({})}>alle Dokumente</button>
          </div>
        </div>
      )}

      <div
        onClick={() => fileInput.current?.click()}
        className="rounded-xl p-5 mb-4 text-sm text-center cursor-pointer transition-colors"
        style={{
          border: `2px dashed ${dragOver ? 'var(--badge-green-text)' : 'var(--surface-border)'}`,
          color: 'var(--text-muted)',
          background: dragOver ? 'var(--surface-bg)' : 'transparent',
        }}>
        PDF oder Foto hierher ziehen
        {transactionId ? '' : ` — wird als ${KIND_LABEL[kind ?? 'RECEIPT']} abgelegt`}. Unterwegs scannt die App.
      </div>

      {!transactionId && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Chip active={!kind} onClick={() => { setKind(undefined); }}>Alle</Chip>
          {KINDS.map(k => (
            <Chip key={k} active={kind === k} onClick={() => { setKind(k); if (k !== 'RECEIPT') setUnmatched(false); }}>
              {KIND_PLURAL[k]}
            </Chip>
          ))}
          <span className="mx-1" style={{ color: 'var(--surface-border)' }}>|</span>
          <Chip active={unmatched} onClick={() => { setUnmatched(u => !u); setKind('RECEIPT'); }}>
            Belege ohne Umsatz
          </Chip>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suche in Aussteller, Betreff, Notiz und Text"
            className="px-3 py-1.5 rounded-lg text-sm border flex-1 min-w-[220px]"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
          />
        </div>
      )}

      <div className="rounded-xl overflow-hidden"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        {loading ? (
          <div className="p-6 flex justify-center"><Spinner /></div>
        ) : documents.length === 0 ? (
          <p className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            {transactionId ? 'An diesem Umsatz hängt noch kein Beleg.' : 'Keine Dokumente für diese Auswahl.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ color: 'var(--text-muted)' }}>
              <tr className="text-left">
                <th className="px-4 py-2 font-medium">Datum</th>
                <th className="px-4 py-2 font-medium">Art</th>
                <th className="px-4 py-2 font-medium">Aussteller / Betreff</th>
                <th className="px-4 py-2 font-medium text-right">Betrag</th>
                <th className="px-4 py-2 font-medium">Kontoumsatz</th>
                <th className="px-4 py-2 font-medium">Quelle</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(d => (
                <tr key={d.id} onClick={() => { setPrefilled({}); setSelected(d); }}
                  className="border-t cursor-pointer hover:opacity-80" style={{ borderColor: 'var(--surface-border)' }}>
                  <td className="px-4 py-2 whitespace-nowrap nums-tabular" style={{ color: 'var(--text-muted)' }}>
                    {formatDate(d.documentDate)}
                  </td>
                  <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>{KIND_LABEL[d.kind]}</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-primary)' }}>
                    {d.issuer || (!d.title && <span style={{ color: 'var(--text-muted)' }}>{d.fileName}</span>)}
                    {d.title && (
                      <div className={d.issuer ? 'text-xs' : ''} style={{ color: d.issuer ? 'var(--text-muted)' : undefined }}>
                        {d.title}
                      </div>
                    )}
                    {d.note && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{d.note}</div>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap nums-tabular font-semibold"
                    style={{ color: 'var(--text-primary)' }}>
                    {d.amountCents != null ? euro(d.amountCents) : '—'}
                    {d.vatRatePercent != null && (
                      <div className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>{d.vatRatePercent} % MwSt</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {d.transactionId ? (
                      <span style={{ color: 'var(--text-primary)' }}>
                        {d.transactionCounterparty}
                        <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>
                          {formatDate(d.transactionBookingDate)}
                        </span>
                      </span>
                    ) : d.kind !== 'RECEIPT' ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : d.amountCents == null || !d.documentDate ? (
                      <Badge variant="amber">Angaben fehlen</Badge>
                    ) : (
                      <Badge variant="purple">wartet auf Auszug</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {SOURCE_LABEL[d.source]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <DocumentDialog
          document={selected}
          prefilled={prefilled}
          onClose={() => { setSelected(null); setPrefilled({}); }}
          onChanged={updated => {
            // Gespeichert heißt geprüft: die Markierungen haben ihren Zweck erfüllt
            setPrefilled({});
            setSelected(updated);
            load();
          }}
          onDiscarded={() => {
            setSelected(null);
            setPrefilled({});
            load();
          }}
        />
      )}
    </div>
  );
};

/** Ansicht der archivierten Datei neben den erfassten Angaben und, bei Belegen, der Zuordnung. */
const DocumentDialog: React.FC<{
  document: DocumentRecord;
  /** Vom Upload ergänzte Felder: werden markiert, damit sie geprüft werden. */
  prefilled?: Prefilled;
  onClose: () => void;
  onChanged: (d: DocumentRecord) => void;
  onDiscarded: () => void;
}> = ({ document: doc, prefilled = {}, onClose, onChanged, onDiscarded }) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<TransactionCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    kind: doc.kind,
    documentDate: doc.documentDate ?? '',
    title: doc.title ?? '',
    amount: centsToInput(doc.amountCents),
    issuer: doc.issuer ?? '',
    vatRatePercent: doc.vatRatePercent != null ? String(doc.vatRatePercent) : '',
    note: doc.note ?? '',
  });
  const isReceipt = form.kind === 'RECEIPT';

  useEffect(() => {
    let url: string | null = null;
    documentService.getFile(doc.id).then(blob => {
      url = URL.createObjectURL(blob);
      setFileUrl(url);
    }).catch(() => toast.error('Datei konnte nicht geladen werden.'));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc.id]);

  useEffect(() => {
    if (doc.kind !== 'RECEIPT') { setCandidates([]); return; }
    documentService.getCandidates(doc.id).then(setCandidates).catch(() => setCandidates([]));
  }, [doc.id, doc.kind, doc.amountCents, doc.documentDate, doc.transactionId]);

  const run = async (action: () => Promise<DocumentRecord | void>, success: string) => {
    setBusy(true);
    try {
      const result = await action();
      toast.success(success);
      if (result) onChanged(result);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const details: DocumentDetailsRequest = {
      kind: form.kind,
      documentDate: form.documentDate || null,
      title: form.title || null,
      issuer: form.issuer || null,
      // Betrag und Steuersatz gibt es nur am Beleg; beim Wechsel zu einer anderen Art fallen sie weg
      amountCents: isReceipt ? inputToCents(form.amount) : null,
      vatRatePercent: isReceipt && form.vatRatePercent !== '' ? Number(form.vatRatePercent) : null,
      note: form.note || null,
    };
    run(() => documentService.update(doc.id, details), 'Gespeichert.');
  };

  const inputStyle = { background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' };
  const labelStyle = { color: 'var(--text-muted)' };
  const isImage = doc.contentType.startsWith('image/');
  const PrefillMark: React.FC<{ field: keyof Prefilled }> = ({ field }) =>
    prefilled[field] ? (
      <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--badge-amber-text)' }}>
        {prefilled[field] === 'FILE' ? 'aus Datei gelesen' : 'vom Umsatz'}
      </span>
    ) : null;
  // Beleg und Abbuchung sollten auf den Cent übereinstimmen - sonst ist es der falsche Beleg oder falsch gelesen
  const amountMismatch = doc.transactionId != null && doc.amountCents != null &&
    doc.transactionAmountCents != null && doc.amountCents !== -doc.transactionAmountCents;

  return (
    <ModalOverlay ariaLabelledBy="document-dialog-title" onKeyDown={e => e.key === 'Escape' && onClose()}>
      <div className="w-[min(1100px,95vw)] rounded-xl p-6 grid md:grid-cols-2 gap-6"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        <div className="rounded-lg overflow-hidden min-h-[400px] flex items-center justify-center"
          style={{ border: '1px solid var(--surface-border)', background: 'var(--input-bg)' }}>
          {!fileUrl ? <Spinner /> : isImage ? (
            <img src={fileUrl} alt={doc.fileName} className="max-h-[75vh] object-contain" />
          ) : doc.contentType === 'application/pdf' ? (
            <iframe src={fileUrl} title={doc.fileName} className="w-full h-[75vh]" />
          ) : (
            <a href={fileUrl} download={doc.fileName} className="underline text-sm">Datei herunterladen</a>
          )}
        </div>

        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2 id="document-dialog-title" className="text-lg font-bold">
              {doc.issuer || doc.title || KIND_LABEL[doc.kind]}
            </h2>
            <button onClick={onClose} className="text-xl leading-none" style={labelStyle}
              aria-label="Schließen">×</button>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {KINDS.map(k => (
              <Chip key={k} active={form.kind === k} onClick={() => setForm(f => ({ ...f, kind: k }))}>
                {KIND_LABEL[k]}
              </Chip>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-xs" style={labelStyle}>{isReceipt ? 'Belegdatum' : 'Datum'}</span>
              {isReceipt && <PrefillMark field="documentDate" />}
              <input type="date" value={form.documentDate}
                onChange={e => setForm(f => ({ ...f, documentDate: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-lg border" style={inputStyle} />
            </label>
            <label className="block">
              <span className="text-xs" style={labelStyle}>{ISSUER_LABEL[form.kind]}</span>
              {isReceipt && <PrefillMark field="issuer" />}
              <input value={form.issuer}
                onChange={e => setForm(f => ({ ...f, issuer: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-lg border" style={inputStyle} />
            </label>
            {isReceipt ? (
              <>
                <label className="block">
                  <span className="text-xs" style={labelStyle}>Betrag (brutto)</span>
                  <PrefillMark field="amountCents" />
                  <input value={form.amount} inputMode="decimal" placeholder="0,00"
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border text-right nums-tabular" style={inputStyle} />
                </label>
                <label className="block">
                  <span className="text-xs" style={labelStyle}>Steuersatz</span>
                  <PrefillMark field="vatRatePercent" />
                  <select value={form.vatRatePercent}
                    onChange={e => setForm(f => ({ ...f, vatRatePercent: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border" style={inputStyle}>
                    <option value="">unbekannt / gemischt</option>
                    <option value="19">19 %</option>
                    <option value="7">7 %</option>
                    <option value="0">0 %</option>
                  </select>
                </label>
              </>
            ) : (
              <label className="block col-span-2">
                <span className="text-xs" style={labelStyle}>{form.kind === 'CONTRACT' ? 'Bezeichnung' : 'Betreff'}</span>
                <input value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-lg border" style={inputStyle} />
              </label>
            )}
          </div>
          {isReceipt && Object.keys(prefilled).length > 0 && (
            <p className="text-xs mb-3" style={labelStyle}>
              Markierte Felder hat DevHub selbst ausgefüllt — bitte mit dem Beleg links vergleichen und speichern.
            </p>
          )}
          <label className="block mb-3">
            <span className="text-xs" style={labelStyle}>
              Notiz{isReceipt ? ' (z. B. Anlass einer Bewirtung)' : ''}
            </span>
            <textarea value={form.note} rows={2}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              className="w-full mt-1 px-3 py-2 rounded-lg border" style={inputStyle} />
          </label>
          <button onClick={save} disabled={busy}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
            Speichern
          </button>

          {(doc.kind === 'RECEIPT' || doc.transactionId) && (
            <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--surface-border)' }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={labelStyle}>
                Kontoumsatz
              </div>
              {doc.transactionId ? (
                <div className="flex items-center justify-between gap-3">
                  <span>
                    {doc.transactionCounterparty} · {formatDate(doc.transactionBookingDate)} ·{' '}
                    {doc.transactionAmountCents != null && euro(doc.transactionAmountCents)}
                    {doc.matchSource === 'AUTO' && (
                      <span className="text-xs ml-2" style={labelStyle}>automatisch</span>
                    )}
                    {amountMismatch && (
                      <span className="block text-xs mt-1" style={{ color: 'var(--badge-amber-text)' }}>
                        Der Betrag des Belegs ({euro(doc.amountCents!)}) weicht von der Abbuchung ab.
                      </span>
                    )}
                  </span>
                  <button disabled={busy} className="text-xs underline" style={labelStyle}
                    onClick={() => run(() => documentService.unmatch(doc.id), 'Zuordnung gelöst.')}>
                    lösen
                  </button>
                </div>
              ) : (
                <p style={labelStyle}>
                  {doc.amountCents == null || !doc.documentDate
                    ? 'Ohne Datum und Betrag ordnet nichts automatisch zu.'
                    : 'Noch kein Umsatz zugeordnet. Der nächste Kontoimport versucht es erneut.'}
                </p>
              )}

              {candidates.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {candidates.map(c => (
                    <li key={c.transactionId} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                      style={{ border: '1px solid var(--surface-border)' }}>
                      <div>
                        <div>{c.counterparty || '—'} · {formatDate(c.bookingDate)} · {euro(c.amountCents)}</div>
                        <div className="text-xs" style={labelStyle}>{c.reason} · {c.accountName}</div>
                      </div>
                      <button disabled={busy} className="text-xs underline whitespace-nowrap"
                        onClick={() => run(() => documentService.match(doc.id, c.transactionId), 'Zugeordnet.')}>
                        zuordnen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-3 text-xs"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-muted)' }}>
            <span title={`SHA-256 ${doc.fileHash}`}>
              {SOURCE_LABEL[doc.source]} · {(doc.fileSize / 1024).toFixed(0)} KB · von {doc.uploadedBy}
            </span>
            <div className="flex gap-4">
              {fileUrl && <a href={fileUrl} download={doc.fileName} className="underline">herunterladen</a>}
              <button disabled={busy} className="underline" style={{ color: 'var(--badge-red-text)' }}
                onClick={async () => {
                  if (!window.confirm('Dokument verwerfen? Die Datei bleibt archiviert, verschwindet aber aus den Listen.')) return;
                  setBusy(true);
                  try {
                    await documentService.discard(doc.id);
                    toast.success('Dokument verworfen.');
                    onDiscarded();
                  } catch {
                    toast.error('Verwerfen fehlgeschlagen.');
                  } finally {
                    setBusy(false);
                  }
                }}>
                verwerfen
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default Documents;
